<script setup>
import DefaultLayout from "@frontJs/layouts/DefaultLayout.vue";
import { CommandLineIcon } from "@heroicons/vue/24/outline";
import {
  DocumentCheckIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  CursorArrowRaysIcon,
  BellAlertIcon,
  DocumentTextIcon,
} from "@heroicons/vue/24/solid";
import GoogleAdsIcon from "@js/components/icons/GoogleAdsIcon.vue";
import MetaIcon from "@js/components/icons/MetaIcon.vue";
import TargetIcon from "@js/components/icons/TargetIcon.vue";
import MenuButton from "@js/components/menus/MenuButton.vue";
import MenuDisclosure from "@js/components/menus/MenuDisclosure.vue";
import MenuGroup from "@js/components/menus/MenuGroup.vue";
import { hasRole } from "@js/utils/permission";
import { useRoute } from "@js/utils/route";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { route, routesAreActive } = useRoute();

const showCustomSidebar = ref(true);

function toggleCustomSidebar() {
  showCustomSidebar.value = !showCustomSidebar.value;
}
</script>

<template>
  <DefaultLayout
    :show-custom-sidebar="showCustomSidebar"
    :toggle-custom-sidebar="toggleCustomSidebar"
  >
    <MenuGroup
      v-slot="{ setCurrentDisclosure }"
      v-if="hasRole('super_admin')"
      class="fixed left-0 top-[4.05rem] z-50 h-[calc(100%-4.05rem)] w-64 overflow-y-auto border-r border-gray-200 bg-indigo-950 px-3 py-4 text-white"
      :class="{
        hidden: !showCustomSidebar,
        'hidden xl:flex': showCustomSidebar,
      }"
    >
      <MenuDisclosure
        v-slot="{ disclosure }"
        :title="t('title.tasks')"
        :icon="DocumentCheckIcon"
        :active="
          routesAreActive([
            'front.projectManagement.task.objectives',
            'front.projectManagement.task.tasks',
            'front.projectManagement.task.createTask',
            'front.projectManagement.task.createTaskIdea',
            'front.projectManagement.task.updateTask',
            'front.projectManagement.task.updateTaskIdea',
            'front.projectManagement.task.taskTimes',
            'front.projectManagement.task.customerTasks',
            'front.projectManagement.task.customerTask',
          ])
        "
        :level="0"
        :set-current-disclosure="setCurrentDisclosure"
        always-open
      >
        <MenuButton
          :title="t('title.apikomTasks')"
          :href="route('front.projectManagement.task.tasks')"
          :active="
            routesAreActive([
              'front.projectManagement.task.tasks',
              'front.projectManagement.task.createTaskIdea',
              'front.projectManagement.task.createTask',
              'front.projectManagement.task.updateTask',
              'front.projectManagement.task.updateTaskIdea',
            ])
          "
          :level="1"
          :disclosure="disclosure"
          :setCurrentDisclosure="setCurrentDisclosure"
        />

        <MenuButton
          :title="t('title.apikomTime')"
          :href="route('front.projectManagement.task.taskTimes')"
          :active="routesAreActive(['front.projectManagement.task.taskTimes'])"
          :level="1"
          :disclosure="disclosure"
          :setCurrentDisclosure="setCurrentDisclosure"
        />

        <MenuButton
          :title="t('title.customerTasks')"
          :href="route('front.projectManagement.task.customerTasks')"
          :active="
            routesAreActive([
              'front.projectManagement.task.customerTasks',
              'front.projectManagement.task.customerTask',
              'front.projectManagement.task.customerTaskIdea',
            ])
          "
          :level="1"
          :disclosure="disclosure"
          :setCurrentDisclosure="setCurrentDisclosure"
        />

        <MenuButton
          :title="t('title.objectives')"
          :href="route('front.projectManagement.task.objectives')"
          :active="routesAreActive(['front.projectManagement.task.objectives'])"
          :level="1"
          :disclosure="disclosure"
          :setCurrentDisclosure="setCurrentDisclosure"
        />
      </MenuDisclosure>

      <MenuDisclosure
        :title="t('title.apikom')"
        :icon="BuildingOffice2Icon"
        :active="
          routesAreActive([
            'front.projectManagement.apikom.affiliations',
            'front.projectManagement.apikom.createAffiliation',
            'front.projectManagement.apikom.updateAffiliation',
            'front.projectManagement.apikom.quotations',
            'front.projectManagement.apikom.createQuotation',
            'front.projectManagement.apikom.updateQuotation',
            'front.projectManagement.apikom.invoicing',
            'front.projectManagement.apikom.packages',
            'front.projectManagement.apikom.createPackage',
            'front.projectManagement.apikom.updatePackage',
            'front.projectManagement.apikom.opportunities',
            'front.projectManagement.apikom.partners',
            'front.projectManagement.apikom.createPartner',
            'front.projectManagement.apikom.updatePartner',
            'front.projectManagement.apikom.prospects',
            'front.projectManagement.apikom.prospect',
            'front.projectManagement.apikom.appointments',
            'front.projectManagement.apikom.createAppointment',
            'front.projectManagement.apikom.updateAppointment',
          ])
        "
        :level="0"
        :set-current-disclosure="setCurrentDisclosure"
      >
        <MenuButton
          :title="t('title.affiliations')"
          :href="route('front.projectManagement.apikom.affiliations')"
          :active="
            routesAreActive([
              'front.projectManagement.apikom.affiliations',
              'front.projectManagement.apikom.createAffiliation',
              'front.projectManagement.apikom.updateAffiliation',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.quotation')"
          :href="route('front.projectManagement.apikom.quotations')"
          :active="
            routesAreActive([
              'front.projectManagement.apikom.quotations',
              'front.projectManagement.apikom.createQuotation',
              'front.projectManagement.apikom.updateQuotation',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.invoicing')"
          :href="route('front.projectManagement.apikom.invoicing')"
          :active="
            routesAreActive(['front.projectManagement.apikom.invoicing'])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.packages')"
          :href="route('front.projectManagement.apikom.packages')"
          :active="
            routesAreActive([
              'front.projectManagement.apikom.packages',
              'front.projectManagement.apikom.createPackage',
              'front.projectManagement.apikom.updatePackage',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.opportunities')"
          :href="route('front.projectManagement.apikom.opportunities')"
          :active="
            routesAreActive(['front.projectManagement.apikom.opportunities'])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.partners')"
          :href="route('front.projectManagement.apikom.partners')"
          :active="
            routesAreActive([
              'front.projectManagement.apikom.partners',
              'front.projectManagement.apikom.createPartner',
              'front.projectManagement.apikom.updatePartner',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.prospects')"
          :href="route('front.projectManagement.apikom.prospects')"
          :active="
            routesAreActive([
              'front.projectManagement.apikom.prospects',
              'front.projectManagement.apikom.prospect',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.appointments')"
          :href="route('front.projectManagement.apikom.appointments')"
          :active="
            routesAreActive([
              'front.projectManagement.apikom.appointments',
              'front.projectManagement.apikom.createAppointment',
              'front.projectManagement.apikom.updateAppointment',
            ])
          "
          :level="1"
        />
      </MenuDisclosure>

      <MenuDisclosure
        :title="t('title.customers')"
        :icon="UserGroupIcon"
        :active="
          routesAreActive([
            'front.projectManagement.customer.customersBudgets',
            'front.projectManagement.customer.contacts',
            'front.projectManagement.customer.createCustomer',
            'front.projectManagement.customer.updateCustomer',
            'front.projectManagement.customer.dev',
            'front.projectManagement.customer.customersServices',
            'front.projectManagement.customer.reports',
          ])
        "
        :level="0"
        :set-current-disclosure="setCurrentDisclosure"
      >
        <MenuButton
          :title="t('title.budgets')"
          :href="route('front.projectManagement.customer.customersBudgets')"
          :active="
            routesAreActive([
              'front.projectManagement.customer.customersBudgets',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.contacts')"
          :href="route('front.projectManagement.customer.contacts')"
          :active="
            routesAreActive([
              'front.projectManagement.customer.contacts',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.dev')"
          :href="route('front.projectManagement.customer.dev')"
          :active="routesAreActive(['front.projectManagement.customer.dev'])"
          :level="1"
        />

        <MenuButton
          :title="t('title.services')"
          :href="route('front.projectManagement.customer.customersServices')"
          :active="
            routesAreActive([
              'front.projectManagement.customer.customersServices',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.reports')"
          :href="route('front.projectManagement.customer.reports')"
          :active="
            routesAreActive(['front.projectManagement.customer.reports'])
          "
          :level="1"
        />
      </MenuDisclosure>

      <MenuDisclosure
        :title="t('title.dev')"
        :icon="CommandLineIcon"
        :active="
          routesAreActive([
            'front.projectManagement.dev.eCommerceModules',
            'front.projectManagement.dev.pageSpeed',
            'front.projectManagement.dev.createPageSpeedUrl',
            'front.projectManagement.dev.servers',
            'front.projectManagement.dev.websites',
            'front.projectManagement.dev.website',
          ])
        "
        :level="0"
        :set-current-disclosure="setCurrentDisclosure"
      >
        <MenuButton
          :title="t('title.servers')"
          :href="route('front.projectManagement.dev.servers')"
          :active="routesAreActive(['front.projectManagement.dev.servers'])"
          :level="1"
        />

        <MenuButton
          :title="t('title.websites')"
          :href="route('front.projectManagement.dev.websites')"
          :active="
            routesAreActive([
              'front.projectManagement.dev.websites',
              'front.projectManagement.dev.website',
            ])
          "
          :level="1"
        />
        <MenuButton
          :title="t('title.pageSpeed')"
          :href="route('front.projectManagement.dev.pageSpeed')"
          :active="
            routesAreActive([
              'front.projectManagement.dev.pageSpeed',
              'front.projectManagement.dev.createPageSpeedUrl',
            ])
          "
          :level="1"
        />
        <MenuButton
          :title="t('title.eCommerceModules')"
          :href="route('front.projectManagement.dev.eCommerceModules')"
          :active="
            routesAreActive(['front.projectManagement.dev.eCommerceModules'])
          "
          :level="1"
        />
      </MenuDisclosure>

      <MenuDisclosure
        :title="t('title.sea')"
        :icon="GoogleAdsIcon"
        :active="
          routesAreActive([
            'front.projectManagement.sea.audiences',
            'front.projectManagement.sea.audience',
          ])
        "
        :level="0"
        :set-current-disclosure="setCurrentDisclosure"
      >
        <MenuButton
          :title="t('title.audiences')"
          :href="route('front.projectManagement.sea.audiences')"
          :active="
            routesAreActive([
              'front.projectManagement.sea.audiences',
              'front.projectManagement.sea.audience',
            ])
          "
          :level="1"
        />
      </MenuDisclosure>

      <MenuDisclosure
        :title="t('title.sma')"
        :icon="MetaIcon"
        :active="
          routesAreActive([
            'front.projectManagement.sma.audiences',
            'front.projectManagement.sma.audience',
          ])
        "
        :level="0"
        :set-current-disclosure="setCurrentDisclosure"
      >
        <MenuButton
          :title="t('title.audiences')"
          :href="route('front.projectManagement.sma.audiences')"
          :active="
            routesAreActive([
              'front.projectManagement.sma.audiences',
              'front.projectManagement.sma.audience',
            ])
          "
          :level="1"
        />
      </MenuDisclosure>

      <MenuDisclosure
        :title="t('title.seo')"
        :icon="CursorArrowRaysIcon"
        :active="
          routesAreActive([
            'front.projectManagement.seo.apikomContents',
            'front.projectManagement.seo.createApikomContent',
            'front.projectManagement.seo.updateApikomContent',
            'front.projectManagement.seo.eCommerceCategoryContents',
            'front.projectManagement.seo.eCommerceProductContents',
            'front.projectManagement.seo.devSeo',
            'front.projectManagement.seo.indexation',
            'front.projectManagement.seo.internalMeshLinks',
            'front.projectManagement.seo.createInternalMeshLink',
            'front.projectManagement.seo.updateInternalMeshLink',
            'front.projectManagement.seo.netlinking',
            'front.projectManagement.seo.createBacklink',
            'front.projectManagement.seo.updateBacklink',
            'front.projectManagement.seo.positionTracking',
            'front.projectManagement.seo.errors',
          ])
        "
        :level="0"
        :set-current-disclosure="setCurrentDisclosure"
      >
        <MenuButton
          :title="t('title.apikomContents')"
          :href="route('front.projectManagement.seo.apikomContents')"
          :active="
            routesAreActive([
              'front.projectManagement.seo.apikomContents',
              'front.projectManagement.seo.createApikomContent',
              'front.projectManagement.seo.updateApikomContent',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.categoryContents')"
          :href="route('front.projectManagement.seo.eCommerceCategoryContents')"
          :active="
            routesAreActive([
              'front.projectManagement.seo.eCommerceCategoryContents',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.productContents')"
          :href="route('front.projectManagement.seo.eCommerceProductContents')"
          :active="
            routesAreActive([
              'front.projectManagement.seo.eCommerceProductContents',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.devSeo')"
          :href="route('front.projectManagement.seo.devSeo')"
          :active="routesAreActive(['front.projectManagement.seo.devSeo'])"
          :level="1"
        />

        <MenuButton
          :title="t('title.indexation')"
          :href="route('front.projectManagement.seo.indexation')"
          :active="routesAreActive(['front.projectManagement.seo.indexation'])"
          :level="1"
        />

        <MenuButton
          :title="t('title.internalMesh')"
          :href="route('front.projectManagement.seo.internalMeshLinks')"
          :active="
            routesAreActive([
              'front.projectManagement.seo.internalMeshLinks',
              'front.projectManagement.seo.createInternalMeshLink',
              'front.projectManagement.seo.updateInternalMeshLink',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.netlinking')"
          :href="route('front.projectManagement.seo.netlinking')"
          :active="
            routesAreActive([
              'front.projectManagement.seo.netlinking',
              'front.projectManagement.seo.createBacklink',
              'front.projectManagement.seo.updateBacklink',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.positionTracking')"
          :href="route('front.projectManagement.seo.positionTracking')"
          :active="
            routesAreActive(['front.projectManagement.seo.positionTracking'])
          "
          :level="1"
        />
      </MenuDisclosure>

      <MenuDisclosure
        :title="t('title.sem')"
        :icon="TargetIcon"
        :active="
          routesAreActive([
            'front.projectManagement.sem.banners',
            'front.projectManagement.sem.banner',
            'front.projectManagement.sem.emailings',
            'front.projectManagement.sem.emailing',
            'front.projectManagement.sem.shortMessageServices',
            'front.projectManagement.sem.shortMessageService',
            'front.projectManagement.sem.events',
            'front.projectManagement.sem.createEvent',
            'front.projectManagement.sem.event',
            'front.projectManagement.sem.tests',
            'front.projectManagement.sem.test',
            'front.projectManagement.sem.segmentations',
            'front.projectManagement.sem.segmentation',
            'front.projectManagement.sem.segmentationCustomers',
            'front.projectManagement.sem.lifeTimeValues',
          ])
        "
        :level="0"
        :set-current-disclosure="setCurrentDisclosure"
      >
        <MenuButton
          :title="t('title.banners')"
          :href="route('front.projectManagement.sem.banners')"
          :active="
            routesAreActive([
              'front.projectManagement.sem.banners',
              'front.projectManagement.sem.banner',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.emailing')"
          :href="route('front.projectManagement.sem.emailings')"
          :active="
            routesAreActive([
              'front.projectManagement.sem.emailings',
              'front.projectManagement.sem.emailing',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.events')"
          :href="route('front.projectManagement.sem.events')"
          :active="
            routesAreActive([
              'front.projectManagement.sem.events',
              'front.projectManagement.sem.createEvent',
              'front.projectManagement.sem.event',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.lifeTimeValues')"
          :href="route('front.projectManagement.sem.lifeTimeValues')"
          :active="
            routesAreActive(['front.projectManagement.sem.lifeTimeValues'])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.sms')"
          :href="route('front.projectManagement.sem.shortMessageServices')"
          :active="
            routesAreActive([
              'front.projectManagement.sem.shortMessageServices',
              'front.projectManagement.sem.shortMessageService',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.segmentations')"
          :href="route('front.projectManagement.sem.segmentations')"
          :active="
            routesAreActive([
              'front.projectManagement.sem.segmentations',
              'front.projectManagement.sem.segmentation',
              'front.projectManagement.sem.segmentationCustomers',
            ])
          "
          :level="1"
        />

        <MenuButton
          :title="t('title.userTests')"
          :href="route('front.projectManagement.sem.tests')"
          :active="
            routesAreActive([
              'front.projectManagement.sem.tests',
              'front.projectManagement.sem.test',
            ])
          "
          :level="1"
        />
      </MenuDisclosure>

      <MenuButton
        :title="t('title.alerts')"
        :href="route('front.projectManagement.alerts')"
        :icon="BellAlertIcon"
        :active="routesAreActive(['front.projectManagement.alerts'])"
        :level="0"
      />

      <MenuButton
        :title="t('title.docAndFaq')"
        :href="route('front.projectManagement.docAndFaq')"
        :icon="DocumentTextIcon"
        :active="
          routesAreActive([
            'front.projectManagement.docAndFaq',
            'front.projectManagement.faqs',
            'front.projectManagement.faq',
          ])
        "
        :level="0"
      />
    </MenuGroup>

    <div :class="{ 'xl:ml-64': showCustomSidebar }">
      <slot />
    </div>
  </DefaultLayout>
</template>
